import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface Language {
  code: string;
  name: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
];

interface LanguageSwitcherProps {
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'sm' | 'default' | 'lg';
  showText?: boolean;
  className?: string;
  dropdownClassName?: string;
}

export function LanguageSwitcher({ 
  variant = 'ghost',
  size = 'default',
  showText = true,
  className = "",
  dropdownClassName = ""
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode).then(() => {
      // Force a re-render by updating local state
      setIsOpen(false);
      // Trigger a page refresh for components that might not be listening to i18n changes
      window.dispatchEvent(new Event('languageChanged'));
    });
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size}
          className={cn(
            "gap-2 min-h-[44px] min-w-[44px]", // Mobile touch targets
            "touch-manipulation", // Optimize touch events
            "transition-all duration-200",
            className
          )}
        >
          <Globe className="w-4 h-4 text-primary flex-shrink-0" />
          {showText && (
            <span className={cn(
              "text-foreground font-medium whitespace-nowrap",
              // Show abbreviated text on small screens, full text on larger screens
              "text-sm",
              // Always show some text for better mobile UX
              ""
            )}>
              {/* Show just flag + code on very small screens, full name on larger */}
              <span className="sm:hidden">{currentLanguage.code.toUpperCase()}</span>
              <span className="hidden sm:inline">{currentLanguage.name}</span>
            </span>
          )}
          <ChevronDown 
            className={cn(
              "w-3 h-3 text-muted-foreground transition-transform duration-200 flex-shrink-0",
              isOpen && "rotate-180"
            )} 
          />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        side="bottom"
        sideOffset={8}
        className={cn(
          "min-w-[160px] p-2", // Increased padding for mobile
          "bg-background border border-border",
          "shadow-lg rounded-lg", // More rounded for mobile
          "z-[9999]", // Much higher z-index to ensure it's above everything
          // Prevent layout shift by using strict positioning
          "!fixed", // Force fixed positioning
          // Mobile-specific positioning improvements  
          "data-[side=bottom]:slide-in-from-top-2",
          "data-[side=top]:slide-in-from-bottom-2", 
          "data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2",
          // Prevent any potential layout shifts
          "transform-gpu",
          "will-change-transform",
          dropdownClassName
        )}
        // Additional props to prevent layout shifts
        avoidCollisions={true}
        collisionPadding={8}
        sticky="always"
      >
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className={cn(
              "flex items-center gap-3 px-4 py-3", // Larger padding for mobile
              "cursor-pointer rounded-lg transition-colors duration-200",
              "min-h-[44px]", // Mobile touch target
              "touch-manipulation",
              "focus:bg-secondary focus:outline-none", // Better focus states
              currentLanguage.code === language.code
                ? "bg-secondary text-primary font-medium"
                : "hover:bg-secondary text-foreground"
            )}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-3 w-full"
            >
              <span className="text-sm font-medium flex-1">{language.name}</span>
              {currentLanguage.code === language.code && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-2 h-2 bg-primary rounded-full flex-shrink-0"
                />
              )}
            </motion.div>
          </DropdownMenuItem>
        ))}
        
        {/* Future Languages Note */}
        <div className="px-4 py-2 border-t border-border mt-1">
          <p className="text-xs text-muted-foreground text-center">
            More languages coming soon
          </p>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 